import { useCallback, useEffect, useState } from 'react';
import { BookOpen, Loader2, Share2 } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import ConfirmActionDialog from '@/components/ConfirmActionDialog';
import { orgMemoryAPI, formatApiError } from '@/lib/api';
import useCapsuleRoutingCatalogs, { estadoDetieneElReloj } from '@/hooks/useCapsuleRoutingCatalogs';

export default function MemoryEventPanel({ tenantId, capsuleId, ticketId, ticketStatus }) {
  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [shareTenant, setShareTenant] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [discardOpen, setDiscardOpen] = useState(false);
  // El estado terminal lo declara el catálogo de la cápsula, no una lista escrita acá: un
  // taller termina sus órdenes en `entregado` y esto le habría dicho que nunca terminan.
  const { ticket_statuses: ticketStatuses } = useCapsuleRoutingCatalogs(tenantId, capsuleId);
  const terminal = estadoDetieneElReloj(ticketStatuses, ticketStatus);

  const load = useCallback(async () => {
    if (!tenantId || !capsuleId || !ticketId) return;
    setLoading(true);
    try {
      const res = await orgMemoryAPI.getByTicket(tenantId, capsuleId, ticketId);
      const ev = res.data?.event || null;
      setEvent(ev);
      setShareTenant(ev?.share_scope === 'tenant');
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [tenantId, capsuleId, ticketId]);

  useEffect(() => {
    load();
  }, [load]);

  const extract = async () => {
    setSaving(true);
    try {
      const res = await orgMemoryAPI.extract(tenantId, capsuleId, ticketId);
      setEvent(res.data?.event || null);
      toast.success('Borrador de memoria extraído');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'No se pudo extraer');
    } finally {
      setSaving(false);
    }
  };

  const saveDraft = async () => {
    if (!event?.event_id) return;
    setSaving(true);
    try {
      const steps = Array.isArray(event.solution_steps)
        ? event.solution_steps
        : String(event.solution_steps || '')
            .split('\n')
            .map((s) => s.trim())
            .filter(Boolean);
      const res = await orgMemoryAPI.patchEvent(tenantId, capsuleId, event.event_id, {
        title: event.title,
        symptoms: event.symptoms,
        root_cause: event.root_cause,
        solution_steps: steps,
        preconditions: event.preconditions,
        share_scope: shareTenant ? 'tenant' : 'capsule',
      });
      setEvent(res.data?.event || event);
      toast.success('Borrador guardado');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  const approve = async () => {
    if (!event?.event_id) return;
    await saveDraft();
    const res = await orgMemoryAPI.approveEvent(tenantId, capsuleId, event.event_id, {
      share_scope: shareTenant ? 'tenant' : 'capsule',
    });
    setEvent(res.data?.event || event);
    toast.success('Promovido a conocimiento colectivo');
  };

  const discard = async () => {
    if (!event?.event_id) return;
    try {
      if (event.status === 'draft' || event.status === 'archived') {
        await orgMemoryAPI.deleteEvent(tenantId, capsuleId, event.event_id, {
          reason: 'discard_from_ticket',
        });
        setEvent(null);
        toast.success('Borrador eliminado');
      } else {
        const res = await orgMemoryAPI.archiveEvent(tenantId, capsuleId, event.event_id, {
          reason: 'discard_from_ticket',
        });
        setEvent(res.data?.event || null);
        toast.success('Suceso archivado (ya no se inyecta ni se recupera)');
      }
    } catch (err) {
      toast.error(formatApiError(err) || 'No se pudo descartar');
      throw err;
    }
  };

  if (!terminal && !event) {
    return (
      <div className="p-4 space-y-3 text-sm text-zinc-600" data-testid="memory-event-panel-waiting">
        <p className="font-medium text-zinc-800">Aún no hay borrador de memoria</p>
        <p className="text-xs text-zinc-500 leading-relaxed">
          Crear el ticket (mail, WhatsApp, chat) <b>no</b> genera un suceso. El borrador aparece
          automáticamente al <b>resolver o cerrar</b>, o podés forzar la extracción ahora.
          Después revisá, guardá y <b>Promové</b> para que entre al libro de historia colectivo
          (visible en Base de Conocimiento → Memoria).
        </p>
        <Button size="sm" className="rounded-sm" onClick={extract} disabled={saving}>
          {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : null}
          Extraer borrador ahora
        </Button>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center gap-2 p-4 text-sm text-zinc-500">
        <Loader2 className="w-4 h-4 animate-spin" /> Cargando memoria…
      </div>
    );
  }

  return (
    <div className="h-full overflow-y-auto p-4 space-y-4" data-testid="memory-event-panel">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="flex items-center gap-2 text-sm font-semibold text-zinc-800">
          <BookOpen className="w-4 h-4" />
          Libro de historia — suceso
        </div>
        <div className="flex flex-wrap gap-2">
          <Button size="sm" variant="outline" className="rounded-sm" onClick={extract} disabled={saving}>
            {event ? 'Re-extraer' : 'Extraer borrador'}
          </Button>
          {event && event.status !== 'approved' && event.status !== 'archived' && (
            <>
              <Button size="sm" variant="outline" className="rounded-sm" onClick={saveDraft} disabled={saving}>
                Guardar
              </Button>
              <Button size="sm" className="rounded-sm" onClick={() => setConfirmOpen(true)} disabled={saving}>
                Promover
              </Button>
            </>
          )}
          {event && event.status !== 'archived' && (
            <Button
              size="sm"
              variant="outline"
              className="rounded-sm text-red-700 border-red-200"
              onClick={() => setDiscardOpen(true)}
              disabled={saving}
            >
              {event.status === 'draft' ? 'Descartar borrador' : 'Archivar'}
            </Button>
          )}
        </div>
      </div>

      {!event ? (
        <p className="text-sm text-zinc-500">
          No hay borrador aún. Al resolver/cerrar se extrae automáticamente; también podés usar
          «Extraer borrador». No depende del tiempo desde que llegó el mail.
        </p>
      ) : (
        <div className="space-y-3">
          <div className="text-xs text-zinc-500">
            Estado: <strong>{event.status}</strong>
            {event.chapter_id ? ` · capítulo ${event.chapter_id.slice(0, 8)}…` : ''}
            {event.confidence != null ? ` · confianza ${Number(event.confidence).toFixed(2)}` : ''}
          </div>
          {/* **Quién escribió esto cambia cómo se lee.** Si lo escribió la plantilla, el texto
              dice "Pendiente de validación humana" y no leyó el caso: revisarlo es escribirlo
              de nuevo, no corregirlo. Sin esta línea las dos cosas se ven idénticas. */}
          {event.provenance?.extraction === 'heuristic' ? (
            <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-sm p-2"
               data-testid="memoria-origen-plantilla">
              Esto lo llenó una <b>plantilla automática</b>, no un modelo: nadie leyó el caso.
              Sirve como formulario en blanco. Antes de promoverlo, escribí vos qué pasó de
              verdad — o pulsá <b>Re-extraer</b> para que el modelo lo lea ahora.
            </p>
          ) : (
            <p className="text-xs text-zinc-500" data-testid="memoria-origen-modelo">
              Lo redactó un modelo leyendo el caso. Revisalo igual: la confianza la puso él.
            </p>
          )}
          <div>
            <Label className="text-xs">Título</Label>
            <Input
              className="rounded-sm"
              value={event.title || ''}
              disabled={event.status === 'approved' || event.status === 'archived'}
              onChange={(e) => setEvent({ ...event, title: e.target.value })}
            />
          </div>
          <div>
            <Label className="text-xs">Síntomas</Label>
            <Textarea
              className="rounded-sm min-h-[80px]"
              value={event.symptoms || ''}
              disabled={event.status === 'approved' || event.status === 'archived'}
              onChange={(e) => setEvent({ ...event, symptoms: e.target.value })}
            />
          </div>
          <div>
            <Label className="text-xs">Causa raíz</Label>
            <Textarea
              className="rounded-sm min-h-[60px]"
              value={event.root_cause || ''}
              disabled={event.status === 'approved' || event.status === 'archived'}
              onChange={(e) => setEvent({ ...event, root_cause: e.target.value })}
            />
          </div>
          <div>
            <Label className="text-xs">Pasos de solución (uno por línea)</Label>
            <Textarea
              className="rounded-sm min-h-[100px]"
              value={
                Array.isArray(event.solution_steps)
                  ? event.solution_steps.join('\n')
                  : event.solution_steps || ''
              }
              disabled={event.status === 'approved' || event.status === 'archived'}
              onChange={(e) => setEvent({ ...event, solution_steps: e.target.value })}
            />
          </div>
          <div>
            <Label className="text-xs">Precondiciones</Label>
            <Textarea
              className="rounded-sm min-h-[60px]"
              value={event.preconditions || ''}
              disabled={event.status === 'approved' || event.status === 'archived'}
              onChange={(e) => setEvent({ ...event, preconditions: e.target.value })}
            />
          </div>
          {event.status !== 'approved' && event.status !== 'archived' && (
            <label className="flex items-center gap-2 text-xs text-zinc-700 cursor-pointer">
              <input
                type="checkbox"
                checked={shareTenant}
                onChange={(e) => setShareTenant(e.target.checked)}
              />
              <Share2 className="w-3.5 h-3.5" />
              Compartir con todo el tenant (otras cápsulas)
            </label>
          )}
        </div>
      )}

      <ConfirmActionDialog
        open={confirmOpen}
        onOpenChange={setConfirmOpen}
        title="Promover a conocimiento colectivo"
        description={
          shareTenant
            ? 'El suceso quedará aprobado e indexado, visible para todo el tenant.'
            : 'El suceso quedará aprobado e indexado solo para esta cápsula.'
        }
        confirmText="Promover"
        onConfirm={approve}
      />

      <ConfirmActionDialog
        open={discardOpen}
        onOpenChange={setDiscardOpen}
        title={event?.status === 'draft' ? 'Descartar borrador' : 'Archivar suceso'}
        description={
          event?.status === 'draft'
            ? 'Se elimina el borrador. No quedará en el libro de historia ni en Sucesos.'
            : 'El suceso deja de recuperarse e inyectarse en agentes. Se purgan vectores en Qdrant.'
        }
        confirmText={event?.status === 'draft' ? 'Eliminar' : 'Archivar'}
        onConfirm={discard}
      />
    </div>
  );
}
